import React, { useCallback, useEffect, useState } from 'react';
import { DefaultButton, PrimaryButton, MessageBar, MessageBarType, Spinner, Dialog, DialogType, DialogFooter, TextField } from '@fluentui/react';
import { getEntityDemo, createEntityDemo, deleteEntityDemo, setEntityDemoPeriod } from '../api/prospectingApi';

function utcDate(value) {
  if (!value) return null;
  const d = new Date(`${String(value).replace(' ', 'T')}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}
function displayDate(value) {
  const d = utcDate(value);
  return d ? d.toLocaleString('pl-PL', { dateStyle: 'medium', timeStyle: 'short' }) : 'nieustalona';
}
function localInput(value) {
  const d = utcDate(value);
  if (!d) return '';
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function apiDate(value) {
  if (!value) return undefined;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) throw new Error('Podaj poprawną datę.');
  return d.toISOString();
}

/** Renders ONLY for opened ONE entity. Never checks Kidplace from a market list. */
export default function DemoKidplacePanel({ token, entityId }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editPeriod, setEditPeriod] = useState(false);
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');

  const refresh = useCallback(async (signal) => {
    const result = await getEntityDemo(token, entityId, signal);
    setData(result);
    setStart(localInput(result?.state?.started_at));
    setEnd(localInput(result?.state?.planned_end_at));
    if (result?.remote?.error) setError(result.remote.error);
    else setError('');
    return result;
  }, [token, entityId]);

  useEffect(() => {
    if (!entityId) return undefined;
    const controller = new AbortController();
    setData(null); setError(''); setNotice(''); setEditPeriod(false); setConfirmDelete(false);
    setLoading(true);
    refresh(controller.signal).catch(e => {
      if (e.name !== 'AbortError') setError(e.message || 'Nie udało się sprawdzić demo Kidplace.');
    }).finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [entityId, refresh]);

  const perform = async (operation, request, success) => {
    if (busy) return;
    setBusy(operation); setError(''); setNotice('');
    try {
      const result = await request();
      setData(previous => ({ ...result, permissions: previous?.permissions }));
      setStart(localInput(result?.state?.started_at));
      setEnd(localInput(result?.state?.planned_end_at));
      setNotice(success(result));
      setEditPeriod(false);
    } catch (e) {
      setError(e.message || 'Nie udało się wykonać operacji.');
    } finally { setBusy(''); }
  };

  const state = data?.state || {};
  const verified = data?.remote?.verified === true;
  const exists = verified ? data?.remote?.exists === true : null;
  const canWrite = Boolean(data?.permissions?.can_create);
  const canDelete = Boolean(data?.permissions?.can_delete);
  const canEditPeriod = Boolean(data?.permissions?.can_edit_period);
  const uncertain = state.status === 'uncertain' || !verified;
  const expired = state.planned_end_at && utcDate(state.planned_end_at)?.getTime() < Date.now();

  return <section className="prospecting-evidence-demo" aria-label="Demonstracja Kidplace" style={{ margin: '14px 0 22px', padding: 16, border: '1px solid #dce5ef', borderRadius: 8 }}>
    <h3 style={{ marginTop: 0 }}>Demo Kidplace</h3>
    {loading ? <Spinner label="Sprawdzam demonstrację w Kidplace…" /> : null}
    {error ? <MessageBar messageBarType={MessageBarType.warning}>{error}</MessageBar> : null}
    {notice ? <MessageBar messageBarType={MessageBarType.success}>{notice}</MessageBar> : null}
    {data && !data.configured ? <MessageBar messageBarType={MessageBarType.warning}>Integracja Kidplace nie jest skonfigurowana po stronie ONE.</MessageBar> : null}
    {data ? <>
      <p className="prospecting-detail-note">
        <b>Status demonstracji:</b> {verified ? (exists ? 'Demo istnieje w Kidplace' : 'Brak demo w Kidplace') : 'Niepotwierdzony — odśwież stan'}
        {state.last_checked_at ? ` · sprawdzono: ${displayDate(state.last_checked_at)}` : ''}
      </p>
      <div className="prospecting-detail-note">
        <div><b>Początek:</b> {displayDate(state.started_at)} {state.period_source === 'manual' ? '(ustalony ręcznie)' : ''}</div>
        <div><b>Planowany koniec:</b> {displayDate(state.planned_end_at)} {expired ? '(minął)' : ''}</div>
        {state.discovered_at && !state.started_at ? <div>Demonstracja odnaleziona w Kidplace {displayDate(state.discovered_at)}. Data jej utworzenia nie jest znana.</div> : null}
        {state.created_by ? <div><b>Uruchomił:</b> {state.created_by}</div> : null}
        {state.deleted_at ? <div><b>Ostatnio usunięto:</b> {displayDate(state.deleted_at)}</div> : null}
      </div>
      <p className="prospecting-detail-note">Okres demonstracji ({data.duration_days || 14} dni dla nowego demo) jest prowadzony w ONE. Upływ terminu nie blokuje automatycznie dostępu w Kidplace.</p>
      <div className="prospecting-evidence-actions" style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {verified && !exists && canWrite ? <PrimaryButton text="Utwórz demo Kidplace" disabled={Boolean(busy) || !data.configured} onClick={() => perform('create', () => createEntityDemo(token, entityId), result => result.result === 'created' ? 'Demo zostało uruchomione.' : 'Demo już istniało — przywrócono dostęp.')} /> : null}
        {verified && exists && canWrite ? <PrimaryButton text="Przywróć dostęp" disabled={Boolean(busy) || !data.configured} onClick={() => perform('restore', () => createEntityDemo(token, entityId), result => result.result === 'created' ? 'Kidplace utworzył nową demonstrację.' : 'Kidplace potwierdził istniejącą demonstrację i przywrócił dostęp.')} /> : null}
        <DefaultButton text={busy === 'refresh' ? 'Sprawdzam…' : 'Odśwież stan'} disabled={Boolean(busy) || loading} onClick={async () => { setBusy('refresh'); setNotice(''); try { await refresh(); } catch (e) { setError(e.message); } finally { setBusy(''); } }} />
        {verified && exists && canDelete ? <DefaultButton text="Usuń demo" disabled={Boolean(busy)} onClick={() => setConfirmDelete(true)} /> : null}
        {canEditPeriod && verified && exists ? <DefaultButton text="Ustal okres" disabled={Boolean(busy)} onClick={() => setEditPeriod(v => !v)} /> : null}
      </div>
      {uncertain ? <div className="prospecting-detail-note">Przed utworzeniem, przywróceniem dostępu lub usunięciem potrzebne jest wiarygodne sprawdzenie bieżącego stanu Kidplace.</div> : null}
      {editPeriod && canEditPeriod ? <div style={{ marginTop: 12 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
          <TextField label="Ustalony początek" type="datetime-local" value={start} onChange={(_, v) => setStart(v || '')} />
          <TextField label="Planowany koniec" type="datetime-local" value={end} onChange={(_, v) => setEnd(v || '')} />
        </div>
        <div style={{ display:'flex', gap: 8, marginTop: 10 }}>
          <PrimaryButton text={busy === 'period' ? 'Zapisuję…' : 'Zapisz okres w ONE'} disabled={Boolean(busy) || (!start && !end)} onClick={() => perform('period', () => setEntityDemoPeriod(token,entityId,{ started_at: apiDate(start), planned_end_at: apiDate(end) }), () => 'Zapisano okres w ONE. Kidplace nie blokuje dostępu automatycznie.')} />
          <DefaultButton text="Anuluj" onClick={() => setEditPeriod(false)} />
        </div>
      </div> : null}
      {(data.history || []).length ? <details style={{ marginTop: 12 }}>
        <summary>Historia operacji ({data.history.length} ostatnich)</summary>
        {(data.history || []).map(row => <div key={row.id} className="prospecting-detail-note">
          {displayDate(row.created_at)} · {row.status} · {row.requested_by}
        </div>)}
      </details> : null}
    </> : null}
    <Dialog hidden={!confirmDelete} onDismiss={() => setConfirmDelete(false)} dialogContentProps={{ type: DialogType.normal, title: 'Usunąć demonstrację Kidplace?', subText: 'To operacja na produkcyjnym Kidplace. Usunięcie wymaga potwierdzenia; zapis historii w ONE pozostanie.' }}>
      <DialogFooter>
        <PrimaryButton text="Tak, usuń demo" disabled={Boolean(busy)} onClick={() => { setConfirmDelete(false); perform('delete', () => deleteEntityDemo(token, entityId), () => 'Kidplace potwierdził usunięcie demo.'); }} />
        <DefaultButton text="Anuluj" onClick={() => setConfirmDelete(false)} />
      </DialogFooter>
    </Dialog>
  </section>;
}
